import { getHTMLTextContent } from '@/lib/util';
import { Choice } from '@/modules/types/choice';
import { QuizQuestion } from '@/modules/types/question';
import {
  Button,
  DeleteOutlinedIcon,
  Dialog,
  DragIndicatorIcon,
  FormItem,
  TextArea,
} from 'elmo-elements';
import React from 'react';
import {
  DragDropContext,
  Draggable,
  DropResult,
  Droppable,
} from 'react-beautiful-dnd';
import { Control, useFormContext } from 'react-hook-form';

interface DragGroupProps {
  choices: Choice[];
  hasDelete?: boolean;
  onDelete: (choice: Choice, index: number) => void;
  control: Control<QuizQuestion, any>;
  onDragEnd: (dropResult: DropResult) => void;
}

export const DragGroup = ({
  choices,
  onDelete,
  onDragEnd,
  hasDelete,
}: DragGroupProps): React.ReactElement => {
  const {
    formState: { errors },
    register,
    watch,
    setValue,
  } = useFormContext<QuizQuestion>();

  const handleChange = (key: any, value: any) => {
    setValue(key, value);
  };

  return (
    <div data-testid="choices-draggable">
      <DragDropContext onDragEnd={onDragEnd}>
        <Droppable droppableId="droppable-draggroup">
          {(provided) => (
            <div
              className="choice-group"
              {...provided.droppableProps}
              ref={provided.innerRef}
            >
              {choices.map((choice, index) => (
                <Draggable
                  key={choice.content + index + choice.choiceId}
                  index={index}
                  draggableId={`draggable-${choice.id}`}
                >
                  {(prividedD) => (
                    <FormItem
                      message={
                        errors.choices?.[index]?.content?.message
                          ? errors.choices?.[index]?.content?.message
                          : ''
                      }
                      status={
                        errors.choices?.[index]?.content?.message
                          ? 'error'
                          : 'default'
                      }
                    >
                      <div
                        className="choice"
                        data-testid="draggable-choice"
                        ref={prividedD.innerRef}
                        {...prividedD.dragHandleProps}
                        {...prividedD.draggableProps}
                      >
                        <DragIndicatorIcon className="margin-sides" />

                        <TextArea
                          {...register(`choices.${index}.content`, {})}
                          onChange={(e: any) =>
                            handleChange(
                              `choices.${index}.content`,
                              e.target.value,
                            )
                          }
                          value={
                            getHTMLTextContent(
                              watch(`choices.${index}.content`),
                            ) ?? ''
                          }
                          name={`choices.${index}.content`}
                          ariaLabel={`Choice ${index}`}
                          id={`elearning-choice-${index}`}
                        />

                        {hasDelete && (
                          <Button
                            ariaLabel="Delete choice"
                            onClick={() => {
                              Dialog.confirm({
                                ariaLabel: 'Delete confirm',
                                title: `Delete Choice ${index + 1}?`,
                                content: `Are you sure you want to delete choice ${
                                  index + 1
                                }?`,
                                onOk() {
                                  onDelete(choice, index);
                                },
                                okLabel: 'Delete',
                              });
                            }}
                          >
                            <DeleteOutlinedIcon />
                          </Button>
                        )}
                      </div>
                    </FormItem>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>
    </div>
  );
};

DragGroup.defaultProps = {
  hasDelete: false,
};

export default DragGroup;
